import { Button, Tooltip } from "@mui/material";
import React, {
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import Guide from "components/Guide";
import styles from "components/Layers/Layers.module.scss";
import InferencingBackdrop from "components/Services/InferencingBackdrop";
import ShotContext from "components/Shot/ShotContext";
import { STATUSES, UserGuides } from "constants/constants";
import { LayerCompact } from "models/Layer";
import { smartVectorsInference } from "services/ShotService";
import { useCountdown } from "utils/hooks";

type Props = {
  aiIcon: ReactNode;
};

const SmartVectorsGenerate: React.FC<Props> = ({ aiIcon }) => {
  const {
    project,
    service,
    layerId,
    layers,
    selectedShot,
    setLayers,
    setSelectedLayer,
    setSmartVectorsReviewPane,
    setReviewLayer,
    throttleUsage,
    getThrottleUsage,
    shotCutouts,
    isAnnotating,
    isAnnotationSaving,
    exitAnnotationModeRef,
  } = useContext(ShotContext);
  const navigate = useNavigate();
  const [isInferencing, setIsInferencing] = useState(false);

  const pendingRoiLayerIds = useMemo(() => {
    if (!layers?.length || !shotCutouts) return [];
    return layers
      .filter((l) => {
        const hasMask = shotCutouts.some(
          (c) => c.layer_id === l.id && c.image_url,
        );
        return hasMask && !l.latest_inference_cutout_status;
      })
      .map((l) => l.id);
  }, [layers, shotCutouts]);

  const hasPendingRoi = pendingRoiLayerIds.length > 0;

  const pendingRoiCount = pendingRoiLayerIds.length;

  const fullShotBlocked = useMemo(() => {
    if (!layers?.length) return false;
    return layers.some(
      (l) =>
        l.smart_vectors_run_type === "full_shot" &&
        (l.latest_inference_cutout_status === STATUSES.IN_PROGRESS ||
          l.latest_inference_cutout_status === STATUSES.COMPLETED),
    );
  }, [layers]);

  const hasGenerateWork = hasPendingRoi || !fullShotBlocked;

  const throttledTill = useMemo(() => {
    if (!throttleUsage?.is_inference_throttled) return null;
    const needed = hasPendingRoi ? pendingRoiCount : 1;
    if (
      (throttleUsage.allowed_inferences || 0) < needed &&
      throttleUsage.next_inference_provision_at
    ) {
      return new Date(throttleUsage.next_inference_provision_at);
    }
    return null;
  }, [throttleUsage, hasPendingRoi, pendingRoiCount]);
  const throttleCountdown = useCountdown(throttledTill, getThrottleUsage);

  const mergeLayer = useCallback(
    (incoming: LayerCompact) => {
      setLayers((oldLayers) => {
        const prev = oldLayers?.find((l) => l.id === incoming.id);
        const merged =
          prev?.cover_image_url &&
          incoming.latest_inference_cutout_status === STATUSES.IN_PROGRESS
            ? { ...incoming, cover_image_url: prev.cover_image_url }
            : incoming;
        return [
          merged,
          ...(oldLayers?.filter((l) => l.id !== incoming.id) || []),
        ].sort((l1, l2) => l2.id - l1.id);
      });
    },
    [setLayers],
  );

  const runInference = useCallback(async () => {
    if (!selectedShot) return;
    setIsInferencing(true);
    try {
      if (hasPendingRoi && pendingRoiLayerIds.length > 0) {
        const results = await Promise.allSettled(
          pendingRoiLayerIds.map((id) =>
            smartVectorsInference(selectedShot.id, {
              use_roi_flow: true,
              layer_id: id,
            }),
          ),
        );
        let failCount = 0;
        results.forEach((r) => {
          if (r.status === "fulfilled") {
            mergeLayer(r.value);
          } else {
            failCount += 1;
          }
        });
        if (failCount > 0) {
          toast.error(
            `${failCount} ROI Motion Vector run(s) failed. Check your connection or try again.`,
          );
        }
      } else {
        const data = await smartVectorsInference(selectedShot.id, undefined);
        mergeLayer(data);
      }
      getThrottleUsage();
      setTimeout(() => {
        setIsInferencing(false);
      }, 6000);
    } catch (e: unknown) {
      setIsInferencing(false);
      const detail =
        typeof e === "object" && e !== null && "detail" in e
          ? String((e as { detail: unknown }).detail)
          : "Motion Vectors request failed.";
      toast.error(detail);
    }
  }, [
    getThrottleUsage,
    hasPendingRoi,
    mergeLayer,
    pendingRoiLayerIds,
    selectedShot,
  ]);

  const handleGenerateMotionVectors = useCallback(async () => {
    if (!selectedShot) return;

    if (isAnnotating) {
      exitAnnotationModeRef.current?.();
      setTimeout(() => {
        runInference();
      }, 150);
      return;
    }

    if (layerId) {
      navigate(`/projects/${project.id}/${service}/shots/${selectedShot.id}`);
      setSelectedLayer(null);
      setSmartVectorsReviewPane(null);
      setReviewLayer(null);
      setTimeout(() => {
        runInference();
      }, 150);
    } else {
      await runInference();
    }
  }, [
    exitAnnotationModeRef,
    isAnnotating,
    layerId,
    navigate,
    project.id,
    runInference,
    selectedShot,
    service,
    setReviewLayer,
    setSelectedLayer,
    setSmartVectorsReviewPane,
  ]);

  const disabled = !hasGenerateWork || !!throttledTill || isAnnotationSaving;

  const disabledReason = useMemo(() => {
    if (throttledTill) return null;
    if (isAnnotationSaving) {
      return "Saving annotation. Wait until it finishes before generating.";
    }
    if (!hasGenerateWork) {
      return "Full shot Motion Vectors have already run or are in progress for this shot, and there are no new ROI masks waiting to generate.";
    }
    return null;
  }, [hasGenerateWork, isAnnotationSaving, throttledTill]);

  if (!selectedShot || layers === undefined) return null;

  const button = (
    <Button
      disabled={disabled}
      className={styles.inferenceBtn}
      onClick={() => {
        handleGenerateMotionVectors();
      }}
    >
      {aiIcon}
      <span>
        {throttleCountdown
          ? `Wait for ${throttleCountdown}`
          : "Generate Motion Vectors"}
      </span>
    </Button>
  );

  return (
    <>
      <Guide
        id={UserGuides.SmartVectorsGenerate}
        open={hasPendingRoi}
        title="Generate Motion Vectors"
        description="Click here to run Motion Vectors on regions with a mask that have not been generated yet, or full shot when applicable."
        placement="top"
      >
        {disabledReason ? (
          <Tooltip title={disabledReason}>
            <span style={{ display: "inline-block", width: "100%" }}>
              {button}
            </span>
          </Tooltip>
        ) : (
          button
        )}
      </Guide>

      {isInferencing && (
        <InferencingBackdrop
          isInferencing={isInferencing}
          heading="Slapshot is generating your Motion Vectors..."
          subHeading="You can continue with other tasks in the meantime."
        />
      )}
    </>
  );
};

export default SmartVectorsGenerate;
